import { useMemo, useContext, useState } from 'react';
import { createColumnHelper, ColumnDef } from '@tanstack/react-table';
import { AdminProfessionals, Cohorts, CorporateClients, Expertises } from '@platform/client';
import {
  AdminProExportResponse,
  AdminProfessional,
  AdminProfessionalApplicationStatus,
  ProfessionalApplicationStatusEnum,
} from '@platform/client/schemas';
import { AdminTable, AdminTableContext, DateCellFromString } from '../../AdminTable';
import { AdminListPageWrapper } from '../../AdminListPage';
import { UpdateCohortModal } from './UpdateCohortModal';
import { SingleProAppStatusModal, BatchProAppStatusModal } from './UpdateProApplicationStatusModal';
import { keepPreviousData } from '@tanstack/react-query';
import { UserEmailCell } from '../../components/UserEmailCell';
import { PillBadge, PillBadgeColors } from '@platform/components';
import { VerifyEmailModal } from './VerifyEmailModal';
import { navigate } from 'wouter/use-browser-location';
import { ADMIN_ROUTES, ROUTES } from '../../../routes/consts';
import { Link } from 'wouter';

export const ProfessionalsAdmin = () => {
  return (
    <AdminListPageWrapper>
      <ProfessionalsAdminContent />
    </AdminListPageWrapper>
  );
};

type UpdateCohortModalData = {
  userId: number;
  currentCohort: string;
  currentPartner: string;
  proName: string;
};

type UpdateAppStatusModalData = {
  applicationId: number;
  status: ProfessionalApplicationStatusEnum;
  userId: number;
  isSapPro: boolean;
};

const columnHelper = createColumnHelper<AdminProfessional>();

const ProfessionalsAdminContent = () => {
  const { rowSelection, adminQueryOptionsV2 } = useContext(AdminTableContext);
  const [updateCohortData, setUpdateCohortData] = useState<UpdateCohortModalData>();
  const [appStatusModalData, setAppStatusModalData] = useState<UpdateAppStatusModalData>();
  const [batchVettingModalOpen, setBatchVettingModalOpen] = useState(false);
  const [emailVerifyUserId, setEmailVerifyUserId] = useState<number | undefined>(undefined);
  const { data, isSuccess, isLoading, isError } = AdminProfessionals.useGetAdminProfessionals(
    {
      ...adminQueryOptionsV2,
    },
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );
  const { professionals = [], filteredCount = 0 } = data || {};

  const { data: expertisesFilterData } = Expertises.useGetExpertises();
  const { data: cohortsFilterData } = Cohorts.useGetCohorts();
  const { data: corpClientsFilterData } = CorporateClients.useGetAllCorporateClients();

  const columns = useMemo(
    () =>
      [
        columnHelper.display({
          id: 'selected',
          header: ({ table }) => (
            <input
              aria-label='toggle select all'
              type='checkbox'
              className='h-4 w-4'
              onChange={table.getToggleAllRowsSelectedHandler()}
              checked={table.getIsAllRowsSelected()}
            />
          ),
          cell: ({ row }) => (
            <div className='flex items-center justify-center'>
              <input
                type='checkbox'
                className='h-4 w-4'
                onChange={row.getToggleSelectedHandler()}
                checked={row.getIsSelected()}
                aria-label='Select Row'
                disabled={row.original.applicationId == null}
              />
            </div>
          ),
          size: 25,
        }),
        columnHelper.accessor('id', {
          header: 'Id',
          cell: (id) => id.getValue(),
          meta: {
            filterVariant: 'text',
          },
          size: 25,
        }),
        columnHelper.accessor('name', {
          header: 'Name',
          cell: ({ row }) => {
            return <ProfessionalLink userId={row.original.id} name={row.original.name} />;
          },
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('email', {
          header: 'Email',
          cell: ({ row }) => (
            <UserEmailCell
              email={row.original.email}
              emailVerified={row.original.emailVerified}
              userId={row.original.id}
              setEmailVerifyUserId={setEmailVerifyUserId}
            />
          ),
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('skills', {
          header: 'Skills',
          cell: (skills) => {
            return skills.getValue().map((skill: string) => (
              <PillBadge variant='plain' color='indigo' key={skill} size='sm'>
                {skill}
              </PillBadge>
            ));
          },
          meta: {
            filterVariant: 'multi-select',
            filterSelectOptions:
              expertisesFilterData?.map((expertise) => ({
                label: expertise.name,
                value: expertise.name,
              })) ?? [],
          },
          enableSorting: false,
        }),
        columnHelper.accessor('clientName', {
          header: 'Corporate',
          cell: (info) => info.getValue(),
          meta: {
            filterVariant: 'multi-select',
            filterSelectOptions:
              corpClientsFilterData?.map((client) => ({
                label: client.name,
                value: client.name,
              })) ?? [],
          },
        }),
        columnHelper.accessor('cohortName', {
          header: 'Cohort',
          cell: (info) => info.getValue(),
          meta: {
            filterVariant: 'multi-select',
            filterSelectOptions:
              cohortsFilterData?.cohorts.map((cohort) => ({
                label: cohort.name,
                value: cohort.name,
              })) ?? [],
          },
        }),
        columnHelper.accessor('applicationStatus', {
          header: 'Pro Status',
          cell: ({ row }) => {
            return (
              <ProAppStatusBadge
                status={row.original.applicationStatus}
                isDisabled={row.original.isDisabled}
                isInactive={row.original.isInactive}
              />
            );
          },
          meta: {
            filterVariant: 'multi-select',
            filterSelectOptions: [
              ...Object.values(ProfessionalApplicationStatusEnum),
              ...['incomplete', 'disabled', 'inactive'],
            ].map((status) => ({
              label: status,
              value: status,
            })),
          },
          enableSorting: false,
        }),
        columnHelper.accessor('createdAt', {
          header: 'Created At',
          cell: (createdAt) => {
            return <DateCellFromString value={createdAt.getValue()} />;
          },
        }),
      ] as ColumnDef<AdminProfessional>[],
    [expertisesFilterData, cohortsFilterData, corpClientsFilterData],
  );

  return (
    <>
      <AdminTable
        columns={columns}
        data={professionals}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        filteredCount={filteredCount}
        enableFiltering={true}
        enableMultiRowSelection={true}
        getRowId={(row) => String(row.id)}
        tableActions={[
          {
            text: 'Batch vetting',
            callback: () => setBatchVettingModalOpen(true),
            isDisabled: () => Object.keys(rowSelection ?? {}).length === 0,
          },
          {
            text: 'Search',
            callback: () => {
              navigate(`platform${ROUTES.ADMIN}${ADMIN_ROUTES.SEARCH_PROFESSIONALS}`);
            },
          },
        ]}
        exportOptions={{
          exportQuery: AdminProfessionals.useGetUserExportData,
          dataToExportFn: (data: AdminProExportResponse) => data.professionals,
          exportFileName: `pro_user_export_${new Date().toISOString().replace(' ', '_')}.csv`,
        }}
      />
      {updateCohortData && (
        <UpdateCohortModal
          userId={updateCohortData.userId}
          currentCohort={updateCohortData.currentCohort}
          currentPartner={updateCohortData.currentPartner}
          onClose={() => setUpdateCohortData(undefined)}
          proName={updateCohortData.proName}
          queryKeyToInvalidate={AdminProfessionals.getGetAdminProfessionalsQueryKey({
            ...adminQueryOptionsV2,
          })}
        />
      )}
      {emailVerifyUserId && (
        <VerifyEmailModal
          userId={emailVerifyUserId}
          onClose={() => setEmailVerifyUserId(undefined)}
        />
      )}
      {appStatusModalData && (
        <SingleProAppStatusModal
          onClose={() => setAppStatusModalData(undefined)}
          applicationId={appStatusModalData.applicationId}
          status={appStatusModalData.status}
          userId={appStatusModalData.userId}
          isSapPro={appStatusModalData.isSapPro}
          queryKeyToInvalidate={AdminProfessionals.getGetAdminProfessionalsQueryKey({
            ...adminQueryOptionsV2,
          })}
        />
      )}
      {batchVettingModalOpen && (
        <BatchProAppStatusModal
          onClose={() => setBatchVettingModalOpen(false)}
          queryKeyToInvalidate={AdminProfessionals.getGetAdminProfessionalsQueryKey({
            ...adminQueryOptionsV2,
          })}
        />
      )}
    </>
  );
};

export const getProBadgeColor = (status: AdminProfessionalApplicationStatus): PillBadgeColors => {
  switch (status) {
    case ProfessionalApplicationStatusEnum.approved:
      return 'success-500';
    case ProfessionalApplicationStatusEnum.rejected:
      return 'orange';
    case ProfessionalApplicationStatusEnum.pending:
      return 'warning';
    case ProfessionalApplicationStatusEnum.waitlisted:
      return 'warning';
    default:
      return 'gray';
  }
};

const ProAppStatusBadge = (props: {
  status: AdminProfessionalApplicationStatus;
  isDisabled?: boolean;
  isInactive?: boolean;
}) => {
  const { status, isDisabled = false, isInactive = false } = props;
  const badgeColor =
    isDisabled ? 'error'
    : isInactive ? 'warning'
    : getProBadgeColor(status);
  const badgeText =
    isDisabled ? 'disabled'
    : isInactive ? 'inactive'
    : status ? status.split('_').join(' ')
    : 'incomplete';
  return (
    <PillBadge variant='plain' color={badgeColor} size='sm'>
      {badgeText}
    </PillBadge>
  );
};

export const ProfessionalLink = ({
  userId,
  name,
}: {
  userId?: number | null;
  name?: string | null;
}) => {
  if (userId && name) {
    return (
      <Link
        to={`~/platform${ROUTES.ADMIN}${ADMIN_ROUTES.PROFESSIONALS}/${userId}`}
        className='text-brand-500 hover:underline'
      >
        {name}
      </Link>
    );
  }
};
